import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { inputClassNames } from '@/components/ui/design';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchEventById, fetchEventByHandle, searchEvents, addOrUpdateCartItemsBulk, addOrUpdateGuestCartItemsBulk, fetchComments, createComment, reportEvent } from '@/apiEndpoints';
import { useAuth } from '@/contexts/auth-context';
import Share from '@/icons/Share';
import Report from '@/icons/Report';
import LikeButton from '@/components/LikeButton';
import ShareModal from '@/components/ShareModal';
import ReportEventModal, { type ReportEventData } from '@/components/ReportEventModal';
import moment from 'moment';
import Minus from '@/icons/Minus';
import Plus from '@/icons/Plus';
import Location from '@/icons/Location';
import Send from '@/icons/Send';
import Loading from '@/icons/Loading';
import { Input } from '@heroui/input';
import { Button } from '@heroui/button';
import { addToast } from '@heroui/toast';
import EventMap from '@/components/EventMap';
import CalendarIcon from '@/icons/Calendar'
import Avatar from '@/components/Avatar';
import SeatMapSelector from '@/components/SeatMapSelector';
import { fetchVenue } from '@/api/venueApi';
import { currencySymbol } from '@/utils/currency';
// removed cart-context import

// Helper function to get site name from environment
const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

// Remove the mock events array and helpers from the main event list
// import { Calendar } from '@heroui/calendar';
// import { CalendarDate, isSameDay } from '@internationalized/date';
// const events: any[] = [];
// function fetchEventById(id: string) {
//     return new Promise((resolve, reject) => {
//         setTimeout(() => {
//             const event = events.find(e => e.title && e.title.replace(/\W+/g, '-').toLowerCase() === id);
//             if (event) resolve(event);
//             else reject(new Error('Event not found'));
//         }, 500);
//     });
// }

// Dynamic tabs based on event data
const getTabs = (event: any) => {
    const baseTabs = ['Date & Time', 'Tickets', 'Location'];

    // Only include Refund Policy tab if event has refund policy
    if (event?.data?.refundPolicy) {
        baseTabs.push('Refund Policy');
    }

    // Only include FAQ tab if event has FAQs
    if (event?.data?.faqs && event?.data?.faqs.length > 0) {
        baseTabs.push('FAQ');
    }

    baseTabs.push('About');
    return baseTabs;
};

export default function IframeEvent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, handle } = useParams();

    // Helper function to get base URL
    const getBaseUrl = () => {
        if (typeof window !== 'undefined') {
            return window.location.origin;
        }
        return '';
    };
    const [ticketCounts, setTicketCounts] = useState<{ [key: string]: number }>({});
    const [showAllTimes, setShowAllTimes] = useState<boolean | 'calendar'>(false);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Seat selection state
    const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
    const [showSeatMap, setShowSeatMap] = useState<boolean>(false);
    const [seatAssignments, setSeatAssignments] = useState<Array<{
        seatUuid: string;
        ticketId: string;
        status: string;
    }>>([]);

    // Handle like changes
    const handleLikeChange = (likes: number, isLiked: boolean) => {
        console.log(`Event like status changed: ${likes} likes, isLiked: ${isLiked}`);
        // You can add additional logic here like updating local state, showing notifications, etc.
    };

    // Seat selection handlers
    const handleSeatSelect = (seatUuid: string, seatNumber: string) => {
        setSelectedSeats(prev => [...prev, seatUuid]);
        console.log(`Selected seat ${seatNumber} (${seatUuid})`);
    };

    const handleSeatDeselect = (seatUuid: string) => {
        setSelectedSeats(prev => prev.filter(id => id !== seatUuid));
        console.log(`Deselected seat ${seatUuid}`);
    };

    const toggleSeatMap = () => {
        setShowSeatMap(prev => !prev);
    };

    // Handle seat assignments loaded from SeatMapSelector
    const handleSeatAssignmentsLoaded = (assignments: Array<{
        seatUuid: string;
        ticketId: string;
        status: string;
    }>) => {
        setSeatAssignments(assignments);
    };

    // Helper function to get all months that contain event dates
    const getAllEventMonths = () => {
        const allDays: Date[] = [];
        event?.data?.times?.forEach((range: any) => {
            const start = new Date(range.startDate);
            const end = new Date(range.endDate);
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                allDays.push(new Date(d));
            }
        });

        // Get unique months from all event days
        const uniqueMonths = new Set<string>();
        allDays.forEach(day => {
            uniqueMonths.add(`${day.getFullYear()}-${day.getMonth()}`);
        });

        // Convert back to Date objects and sort
        return Array.from(uniqueMonths)
            .map(monthStr => {
                const [year, month] = monthStr.split('-').map(Number);
                return new Date(year, month, 1);
            })
            .sort((a, b) => a.getTime() - b.getTime());
    };

    // Add missing state for comment input and reply inputs
    const [commentInput, setCommentInput] = useState('');
    const [replyInputs, setReplyInputs] = useState<{ [key: string]: string }>({});

    // FAQ accordion state
    const [expandedFaqs, setExpandedFaqs] = useState<{ [key: number]: boolean }>({});

    // Toggle FAQ accordion
    const toggleFaq = (index: number) => {
        setExpandedFaqs(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Determine if we're using handle or ID
    const eventIdentifier = handle || id;
    const isHandle = !!handle;

    // Fetch event details
    const { data: event, isLoading, isError, error } = useQuery<any>({
        queryKey: ['event', eventIdentifier, isHandle ? 'handle' : 'id'],
        queryFn: () => isHandle ? fetchEventByHandle(handle as string) : fetchEventById(id as string),
        enabled: !!eventIdentifier,
    });
    const cur = currencySymbol(event?.data?.currency);

    const { data: venueResponse, isLoading: venueLoading } = useQuery({
        queryKey: ['venue', event?.data?.venueId],
        queryFn: () => {
            if (!event?.data?.venueId) {
                throw new Error('Venue ID is required');
            }
            return fetchVenue(event.data.venueId);
        },
        enabled: !!event?.data?.venueId,
    });



    // Update currentMonth when event data is loaded
    useEffect(() => {
        if (event?.data?.times) {
            const allMonths = getAllEventMonths();
            if (allMonths.length > 0) {
                setCurrentMonth(allMonths[0]);
            }
        }
    }, [event?.data?.times]);

    // Handle scroll to show/hide back to top button
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            // Show back to top button when user scrolls down more than 300px
            const shouldShowBackToTop = scrollTop > 300;
            setShowBackToTop(shouldShowBackToTop);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Tickets are now included in event data
    const tickets = event?.data?.tickets || [];


    // Fetch related events (by category or organizer)
    const { data: relatedEventsData = { events: [] } } = useQuery({
        queryKey: ['relatedEvents', event?.data?.category, event?.data?.organizer?._id],
        queryFn: () => {
            if (event?.data?.category) {
                return searchEvents({ category: event?.data?.category, limit: 4 });
            } else if (event?.data?.userId) {
                return searchEvents({ organizerId: event?.data?.userId, limit: 4 });
            }
            return { events: [] };
        },
        enabled: false,
    });
    const relatedEvents = (relatedEventsData?.events || []).filter((e: any) => e.id !== event?.data?._id).slice(0, 4);

    // Fetch comments/messages for this event
    // FIXED: Now using consistent event?.data?._id across all API calls
    const { data: commentsData = { data: [] }, refetch: refetchComments } = useQuery({
        queryKey: ['comments', event?.data?._id],
        queryFn: () => fetchComments(event?.data?._id, 1, 50), // Fetch first 50 comments
        enabled: !!event?.data?._id && event?.data?.commentsDisabled === 0,
    });
    const comments = commentsData?.data || [];

    // Post comment mutation
    const postComment = useMutation({
        mutationFn: async (text: string) => {
            return createComment(event?.data?._id, text);
        },
        onSuccess: () => {
            // Refetch comments to get the updated list
            refetchComments();
            setCommentInput('');
        },
        onError: (error) => {
            console.error('Failed to post comment:', error);
            // You could add a toast notification here
        },
    });

    // Post reply mutation
    const postReply = useMutation({
        mutationFn: async ({ parentCommentId, text }: { parentCommentId: string; text: string }) => {
            return createComment(event?.data?._id, text, parentCommentId);
        },
        onSuccess: (_, { parentCommentId }) => {
            refetchComments();
            setReplyInputs((r) => ({ ...r, [parentCommentId]: '' }));
        },
        onError: (error) => {
            console.error('Failed to post reply:', error);
            // You could add a toast notification here
        },
    });

    // Add checkout mutation
    const checkoutMutation = useMutation({
        mutationFn: async () => {
            const items: Array<{ eventId: string; ticketId: string; quantity: number; selectedDate?: string; seatUuid?: string }> = [];
            const eventId = event?.data?._id;
            if (!eventId) {
                throw new Error('Event ID not found. Please refresh the page and try again.');
            }
            const selectedDate = formatSelectedDate(selectedTime);

            // Add all selected tickets to cart
            // FIXED: Now using consistent event?.data?._id across all API calls
            (tickets || []).forEach((ticket: any, i: number) => {
                const ticketId = ticket._id || i;
                const count = ticketCounts[ticketId] || 0;
                if (count > 0) {
                    items.push({
                        eventId,
                        ticketId: ticket._id,
                        quantity: count,
                        selectedDate,
                    });
                }
            });

            // Add selected seats to cart
            if (event?.data?.venueId && selectedSeats.length > 0) {
                selectedSeats.forEach((seatUuid) => {
                    const seatAssignment = seatAssignments?.find(assignment => assignment.seatUuid === seatUuid);
                    if (seatAssignment) {
                        items.push({
                            eventId,
                            ticketId: seatAssignment.ticketId,
                            quantity: 1,
                            selectedDate,
                            seatUuid,
                        });
                    }
                });
            }

            // Validate that we have items to add
            if (items.length === 0) {
                throw new Error('Please select at least one ticket or seat before checking out.');
            }

            // Ensure we have authentication or guest cart ID
            if (isAuthenticated) {
                return await addOrUpdateCartItemsBulk({ items });
            }
            if (guestCartId) {
                return await addOrUpdateGuestCartItemsBulk({ guestCartId, items });
            }
            
            // If neither authenticated nor guestCartId exists, throw an error
            throw new Error('Unable to add items to cart. Please refresh the page and try again.');
        },
        onSuccess: async () => {
            // Refetch or update cart context after mutation
            // Option 1: Refetch cart items and update context
            // Option 2: If you have the new cart, update context directly
            // Here, refetching is safer
            if (event?.data?.userId) {
                // Placeholder for cart context update
            }
            // Redirect to cart page
            navigate('/checkout');
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add items to cart. Please try again.';
            addToast({
                title: 'Checkout Error',
                description: errorMessage,
                color: 'danger',
            });
        },
    });

    // AddOrUpdateCartItem mutation for increasing ticket count (single request, supports multiple items)
    const addToCartMutation = useMutation({
        mutationFn: async ({
            eventId,
            items,
        }: {
            eventId: string;
            items: Array<{ ticketId: string; quantity: number; seatUuid?: string }>;
        }) => {
            const selectedDate = formatSelectedDate(selectedTime);
            const payloadItems = items.map(i => ({ ...i, eventId, selectedDate }));

            if (isAuthenticated) {
                return addOrUpdateCartItemsBulk({ items: payloadItems });
            }
            if (guestCartId) {
                return addOrUpdateGuestCartItemsBulk({ guestCartId, items: payloadItems });
            }
        },
        onSuccess: () => {
            // Optionally refetch cart or update context if needed
        },
    });

    // Report event mutation
    const reportEventMutation = useMutation({
        mutationFn: async (reportData: ReportEventData) => {
            if (!event?.data?._id) {
                throw new Error('Event ID not found');
            }
            return reportEvent(event.data._id, reportData.reason, reportData.description);
        },
        onSuccess: () => {
            setShowReportModal(false);
            addToast({
                title: 'Report Submitted',
                description: 'Your report has been submitted successfully. Thank you for helping us maintain a safe community.',
                color: 'success',
            });
        },
        onError: (error: any) => {
            setShowReportModal(false);
            console.error('Failed to report event:', error);
            addToast({
                title: 'Report Failed',
                description: error?.response?.data?.message || 'Failed to submit your report. Please try again later.',
                color: 'danger'
            });
        },
    });

    // Remove CalendarDate helper and any linter errors

    // Helper to format date as 'YYYY/MM/DD HH:mm'
    function formatSelectedDate(dateString: string | null) {
        console.log('ddd', dateString);
        if (!dateString) return '';
        const d = new Date(dateString);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    // Compute if at least one ticket is selected
    const hasSelectedTickets = Object.values(ticketCounts).some(count => count > 0);
    const hasSelectedSeats = event?.data?.venueId && selectedSeats.length > 0;
    const hasAnySelection = hasSelectedTickets || hasSelectedSeats;
    const [showCheckoutWarning, setShowCheckoutWarning] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ date?: boolean; tickets?: boolean }>({});
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);

    // Check if selected time is in the past
    const isSelectedTimePassed = selectedTime ? new Date(selectedTime) < new Date() : false;

    // Event status logic based on dates
    const getEventStatus = () => {
        if (!event?.data?.times || event.data.times.length === 0) return 'unknown';

        const now = new Date();
        const allEndDates = event.data.times.map((time: any) => new Date(time.endDate));
        const allStartDates = event.data.times.map((time: any) => new Date(time.startDate));

        // Check if all end dates have passed
        const allEnded = allEndDates.every((endDate: Date) => endDate < now);
        if (allEnded) return 'passed';

        // Check if no start dates have been reached yet
        const anyStarted = allStartDates.some((startDate: Date) => startDate <= now);
        if (!anyStarted) return 'new';

        return 'ongoing';
    };

    const eventStatus = getEventStatus();
    const isEventPassed = eventStatus === 'passed';
    const isEventNew = eventStatus === 'new';
    const isEventOngoing = eventStatus === 'ongoing';

    // Calculate total price
    const regularTicketPrice = (tickets || []).reduce((sum: number, ticket: any, i: number) => {
        const ticketId = ticket._id || i;
        const count = ticketCounts[ticketId] ?? 0;
        return sum + (count * (Number(ticket.price) || 0));
    }, 0);

    // Calculate seat selection total price if venueId exists
    const seatSelectionPrice = event?.data?.venueId && selectedSeats.length > 0
        ? selectedSeats.reduce((total, seatUuid) => {
            const seatAssignment = seatAssignments?.find(assignment => assignment.seatUuid === seatUuid);
            const ticketInfo = seatAssignment ? tickets?.find((ticket: any) => ticket._id === seatAssignment.ticketId) : null;
            const price = ticketInfo?.price ? Number(ticketInfo.price) : 0;
            return total + (isNaN(price) ? 0 : price);
        }, 0)
        : 0;

    const totalPrice = Number(regularTicketPrice) + Number(seatSelectionPrice);

    const { cart, isAuthenticated, guestCartId } = useAuth();

    // Remove the useEffect that preselects tickets from the cart

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col bg-[#f8fafc]">

                <div className="flex-1 flex items-center justify-center text-lg text-gray-400">Loading event...</div>

            </div>
        );
    }

    // Handle event not found or error
    if (isError || !event?.data) {
        return (
            <div className="min-h-screen flex flex-col bg-[#f8fafc]">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-6xl mb-4">🔍</div>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">Event Not Found</h1>
                        <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or may have been removed.</p>
                        <button
                            onClick={() => window.history.back()}
                            className="bg-primary50 text-white px-6 py-2 rounded hover:bg-primary60 transition-colors dark:bg-nightAccent dark:text-[#080808] dark:hover:bg-nightAccent/90"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    // Related events: up to 4 others, excluding the current event
    // const relatedEvents: any[] = []; // No mock data, so no related events

    const sectionIds: Record<string, string> = {
        'Date & Time': 'date-time',
        'Tickets': 'tickets',
        'Location': 'location',
        'How to get there & Parking': 'how-to-get-there',
        'Refund Policy': 'refund-policy',
        'FAQ': 'faq',
        'About': 'about',
    };

    // Helper to convert Date to CalendarDate
    // function toCalendarDate(date: Date) {
    //     return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    // }

    return (
        <div className="min-h-screen flex flex-col bg-[#fff]" style={{ overscrollBehavior: 'none' }}>
            <Helmet>
                <title>{event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`}</title>
                <meta name="description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta name="keywords" content={`${event?.data?.title}, event, ${event?.data?.location?.address}, ${event?.data?.organizer?.orgName}, ${getSiteName()}`} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="event" />
                <meta property="og:title" content={event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`} />
                <meta property="og:description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta property="og:image" content={event?.data?.attachments?.banner?.link || '/bannerevent.png'} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content={getSiteName()} />

                {/* Event-specific Open Graph tags */}
                {event?.data?.times?.[0]?.startDate && (
                    <meta property="event:start_time" content={new Date(event.data.times[0].startDate).toISOString()} />
                )}
                {event?.data?.times?.[0]?.endDate && (
                    <meta property="event:end_time" content={new Date(event.data.times[0].endDate).toISOString()} />
                )}
                {event?.data?.location?.address && (
                    <meta property="event:location" content={event.data.location.address} />
                )}

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`} />
                <meta name="twitter:description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta name="twitter:image" content={event?.data?.attachments?.banner?.link || '/bannerevent.png'} />

                {/* Additional meta tags */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content={event?.data?.organizer?.orgName || getSiteName()} />
                <link rel="canonical" href={window.location.href} />

                {/* Event structured data for search engines */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Event",
                        "name": event?.data?.title || "Event",
                        "description": event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'}`,
                        "image": event?.data?.attachments?.banner?.link || '/bannerevent.png',
                        "startDate": event?.data?.times?.[0]?.startDate,
                        "endDate": event?.data?.times?.[0]?.endDate,
                        "location": event?.data?.location?.address ? {
                            "@type": "Place",
                            "name": event.data.location.address,
                            "address": event.data.location.address
                        } : undefined,
                        "organizer": event?.data?.organizer?.orgName ? {
                            "@type": "Organization",
                            "name": event.data.organizer.orgName
                        } : undefined,
                        "offers": event?.data?.tickets?.length > 0 ? {
                            "@type": "Offer",
                            "availability": "https://schema.org/InStock",
                            "price": event.data.tickets[0]?.price || "0",
                            "priceCurrency": "USD"
                        } : undefined
                    })}
                </script>
            </Helmet>
            <style>{`
                .calendar-passed-date {
                    background-color: #fef2f2 !important;
                    color: #dc2626 !important;
                    border: 1px solid #fecaca !important;
                }
                .calendar-passed-date:hover {
                    background-color: #fef2f2 !important;
                    color: #dc2626 !important;
                }
            `}</style>

            <div className="container mx-auto px-4 pt-6 pb-32 md:pb-12 flex flex-col gap-3">
                {/* Banner */}
                <div className="rounded overflow-hidden w-full h-[196px] sm:h-[464px] relative">
                    {/* Blurred background image */}
                    <img
                        src={event?.data?.attachments?.banner?.link || '/bannerevent.png'}
                        alt="Event Banner Background"
                        className="absolute inset-0 w-full h-full object-cover max-md:hidden scale-110 opacity-60"
                    />
                    {/* Main sharp image */}
                    <img
                        src={event?.data?.attachments?.banner?.link || '/bannerevent.png'}
                        alt="Event Banner"
                        className="relative z-10 w-full h-full object-contain bg-transparent object-center"
                    />
                </div>

                {/* Main Content */}
                <div className="flex flex-col md:flex-row gap-[64px]">


                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                        {/* Tabs */}
                        <div className="flex-wrap hidden sm:flex gap-2 shadow-eventopia rounded h-[56px] p-2 mb-3">
                            {getTabs(event).map(t => (
                                <button
                                    key={t}
                                    onClick={() => {
                                        const element = document.getElementById(sectionIds[t]);
                                        if (element) {
                                            const headerOffset = 100; // Adjust this value based on your header height
                                            const elementPosition = element.getBoundingClientRect().top;
                                            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                                            window.scrollTo({
                                                top: offsetPosition,
                                                behavior: 'smooth'
                                            });
                                        }
                                    }}
                                    className={`px-4 py-2 rounded-t font-medium text-sm transition-all text-gray-500 hover:text-primary50 hover:border-primary50 ${typeof window !== 'undefined' && window.location.hash === '#' + sectionIds[t] ? 'text-primary50 bg-white' : 'border-transparent bg-transparent'}`}
                                >{t}</button>
                            ))}
                            <div className="flex-1" />
                            <button
                                onClick={() => setShowShareModal(true)}
                                className="ml-auto px-3 py-2 flex items-center gap-2 text-gray-500 hover:text-primary50 text-sm"
                            >
                                Share <Share />
                            </button>
                            <LikeButton
                                eventId={event?.data?._id || event?.data?.id || ''}
                                initialLikes={event?.data?.likes || 0}
                                likeStats={event?.data?.likeStats}
                                className="text-grey20 bg-grey99 px-3 h-[40px] rounded font-semibold text-xs hidden sm:flex"
                                refetchQueries={['event', 'relatedEvents']}
                                onLikeChange={handleLikeChange}
                            />
                        </div>
                        {/* Title, Like, Status Badges */}
                        <div className="flex items-center gap-3 mb-2 ">
                            {/* {isEventNew && <span className="bg-[#CB0000] text-white text-xs font-semibold px-2 py-1 h-[24px] rounded">New</span>}
                            {isEventPassed && <span className="bg-gray-500 text-white text-xs font-semibold px-2 py-1 h-[24px] rounded">Passed</span>}
                            {isEventOngoing && <span className="bg-primary50 text-white text-xs font-semibold px-2 py-1 h-[24px] rounded">Ongoing</span>} */}
                            <LikeButton
                                eventId={event?.data?._id || event?.data?.id || ''}
                                initialLikes={event?.data?.likes || 0}
                                likeStats={event?.data?.likeStats}
                                className="text-grey20 bg-grey99 px-3 h-[28px] rounded font-semibold text-xs sm:hidden"
                                refetchQueries={['event', 'relatedEvents']}
                                onLikeChange={handleLikeChange}
                            />
                            <button
                                onClick={() => setShowShareModal(true)}
                                className="gap-2 sm:hidden ml-auto flex items-center px-3 py-2 text-gray-500 hover:text-primary50 text-sm"
                            >
                                Share <Share />
                            </button>
                        </div>
                        <h1 className="font-vazirmatn text-xl md:text-3xl text-grey20 font-bold mb-4 sm:mb-8 leading-tight" >{event?.data?.title}</h1>
                        {/* All Tab Sections */}
                        <div id="date-time" className="mb-6 scroll-mt-24">
                            <div className="font-bold text-grey40 text-lg sm:text-xl mb-3 sm:mb-1">Date & Time</div>
                            {validationErrors.date && (
                                <div className="text-red-500 text-sm mb-2 flex items-center gap-2">
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor" />
                                    </svg>
                                    Please select a date and time
                                </div>
                            )}
                            {(() => {
                                // Expand all dateRanges to individual days
                                const allDays: Date[] = [];
                                event?.data?.times?.forEach((range: any) => {

                                    const start = new Date(range.startDate);
                                    const end = new Date(range.endDate);

                                    for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
                                        allDays.push(new Date(d));
                                    }
                                });



                                allDays.sort((a, b) => a.getTime() - b.getTime());
                                const now = new Date();
                                const upcoming = allDays.filter(d => d >= now);
                                console.log(upcoming);

                                // Auto-select the date if there's only one available
                                if (upcoming.length === 1 && !selectedTime) {
                                    setSelectedTime(upcoming[0].toISOString());
                                }

                                const first5 = upcoming.slice(0, 5);
                                const moreCount = Math.max(0, upcoming.length - 5);

                                // For selection

                                // Calculate overall event start and end dates
                                const allStartDates = event?.data?.times?.map((range: any) => new Date(range.startDate)) || [];
                                const allEndDates = event?.data?.times?.map((range: any) => new Date(range.endDate)) || [];
                                const eventStartDate = allStartDates.length > 0 ? new Date(Math.min(...allStartDates.map((d: Date) => d.getTime()))) : null;
                                const eventEndDate = allEndDates.length > 0 ? new Date(Math.max(...allEndDates.map((d: Date) => d.getTime()))) : null;

                                // Check if start and end dates are the same day
                                const isSameDay = eventStartDate && eventEndDate &&
                                    eventStartDate.getFullYear() === eventEndDate.getFullYear() &&
                                    eventStartDate.getMonth() === eventEndDate.getMonth() &&
                                    eventStartDate.getDate() === eventEndDate.getDate();

                                // Get the earliest start time and latest end time for the same day
                                const getTimeRangeForSameDay = () => {
                                    if (!isSameDay) return null;

                                    const sameDayRanges = event?.data?.times?.filter((range: any) => {
                                        const start = new Date(range.startDate);
                                        return start.getFullYear() === eventStartDate.getFullYear() &&
                                            start.getMonth() === eventStartDate.getMonth() &&
                                            start.getDate() === eventStartDate.getDate();
                                    }) || [];

                                    if (sameDayRanges.length === 0) return null;

                                    const startTimes = sameDayRanges.map((range: any) => new Date(range.startDate));
                                    const endTimes = sameDayRanges.map((range: any) => new Date(range.endDate));

                                    const earliestStart = new Date(Math.min(...startTimes.map((d: Date) => d.getTime())));
                                    const latestEnd = new Date(Math.max(...endTimes.map((d: Date) => d.getTime())));

                                    return {
                                        start: earliestStart,
                                        end: latestEnd
                                    };
                                };

                                const timeRange = getTimeRangeForSameDay();

                                return (
                                    <>
                                        {eventStartDate && eventEndDate && (
                                            <div className="text-[15px] text-grey30 mb-3 sm:mb-2 flex flex-row gap-2">
                                                <CalendarIcon />
                                                {isSameDay ? (
                                                    <>
                                                        {eventStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                        {timeRange && (
                                                            <span className="text-grey20">
                                                                {moment(timeRange.start).format('hh:mm A')} - {moment(timeRange.end).format('hh:mm A')}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {eventStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {eventEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex gap-5 overflow-x-scroll overflow-y-hidden mb-4">
                                            {first5.map((d, i) => {
                                                const isSelected = selectedTime === d.toISOString();
                                                const isDatePassed = d < new Date();
                                                const isDisabled = isEventPassed || isDatePassed;
                                                console.log('wwww', selectedTime, d.toISOString());
                                                return (
                                                    <button
                                                        key={i}
                                                        className={`flex flex-col justify-between items-center px-3 py-[14px] rounded min-w-[70px] h-[152px] w-[116px] bg-[#F8F8F8] transition-colors ${isSelected ? 'border-primary50 border-2' : validationErrors.date ? 'border-red-300 border-2 bg-red-50' : 'border-gray-200 hover:bg-gray-50 border-1.5'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        onClick={() => {
                                                            if (!isDisabled) {
                                                                setSelectedTime(d.toISOString());
                                                                // Clear date validation error when user selects a date
                                                                if (validationErrors.date) {
                                                                    setValidationErrors(prev => ({ ...prev, date: false }));
                                                                }
                                                            }
                                                        }}
                                                        disabled={isDisabled}
                                                    >
                                                        <span className="text-[15px] font-bold text-grey30 mb-0.5">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                                        <span className="text-[13px] text-grey20 mb-0.5">{d.toLocaleDateString('en-US', { month: 'short' })}</span>
                                                        <span className={`w-10 h-10 flex items-center justify-center rounded mb-1 text-[17px] font-bold ${isSelected ? 'bg-primary50 text-white' : isDisabled ? 'bg-gray-300 text-gray-500' : 'bg-primary99 text-grey20'}`}>{d.getDate()}</span>
                                                        <span className="text-sm text-grey20">{moment(d).format('hh:mm A')}</span>
                                                        {isDatePassed && !isEventPassed && (
                                                            <span className="text-xs text-red-500 font-semibold">Passed</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                            {/* {moreCount > 0 && (
                                                <button className="px-4 py-2 rounded border border-gray-200 text-xs font-medium bg-white text-gray-700 whitespace-nowrap ml-2 self-center" onClick={() => setShowAllTimes(true)}>+{moreCount} more</button>
                                            )} */}
                                            {moreCount > 5 &&
                                                <button
                                                    className={`flex flex-col items-center gap-4 justify-center px-3 py-2 rounded border min-w-[70px] w-[116px] h-[152px] bg-[#F8F8F8] transition-colors ${isEventPassed ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    onClick={() => !isEventPassed && setShowAllTimes('calendar')}
                                                    disabled={isEventPassed}
                                                >
                                                    <CalendarIcon color={'#748586'} width={24} height={24} />
                                                    <span className="text-[17px] font-bold text-primary40 mt-0.5 max-w-[70px]">More Options</span>
                                                </button>
                                            }
                                        </div >
                                        {selectedTime && (
                                            <div className="bg-primary99 border border-primary50 rounded p-3 mb-2">
                                                <div className="text-sm font-semibold text-primary50 mb-1">Selected Date & Time</div>
                                                <div className="text-sm text-grey20">
                                                    {new Date(selectedTime).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })} at {moment(new Date(selectedTime)).format('hh:mm A')}
                                                </div>
                                            </div>
                                        )
                                        }
                                        {/* Popup for all times */}
                                        {showAllTimes === true && (
                                            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                                                <div className="bg-white rounded p-6 max-h-[80vh] overflow-y-auto min-w-[300px]">
                                                    <div className="font-semibold mb-2">All Dates & Times</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {upcoming.map((d, i) => {

                                                            const isSelected = selectedTime === d.toISOString();
                                                            return (
                                                                <button key={i} className={`flex flex-col items-center px-3 py-2 rounded border min-w-[70px] bg-white transition-colors ${isSelected ? 'border-primary50 bg-[#e0f7f5]' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => { setSelectedTime(d.toISOString()); setShowAllTimes(false); }}>
                                                                    <span className="text-xs font-semibold text-gray-700 mb-0.5">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                                                    <span className="text-xs font-semibold text-gray-700 mb-0.5">{d.toLocaleDateString('en-US', { month: 'short' })}</span>
                                                                    <span className={`w-8 h-8 flex items-center justify-center rounded-full mb-1 text-base font-bold ${isSelected ? 'bg-primary50 text-white' : 'bg-gray-100 text-primary50'}`}>{d.getDate()}</span>
                                                                    <span className="text-xs text-gray-500">{d.toLocaleTimeString()}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <button className="mt-4 px-4 py-2 rounded border border-gray-200 text-xs font-medium bg-white text-gray-700" onClick={() => setShowAllTimes(false)}>Close</button>
                                                </div>
                                            </div>
                                        )}
                                        {/* Popup for calendar */}
                                        {showAllTimes === 'calendar' && (
                                            <div onClick={() => setShowAllTimes(false)} className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                                                <div onClick={(e) => e.stopPropagation()} className="bg-white rounded flex flex-col">
                                                    <div className="p-4">
                                                        {/* Month Navigation */}
                                                        <div className="flex items-center justify-between mb-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const allMonths = getAllEventMonths();
                                                                    const currentIndex = allMonths.findIndex(m =>
                                                                        m.getFullYear() === currentMonth.getFullYear() &&
                                                                        m.getMonth() === currentMonth.getMonth()
                                                                    );
                                                                    if (currentIndex > 0) {
                                                                        setCurrentMonth(allMonths[currentIndex - 1]);
                                                                    }
                                                                }}
                                                                className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                                                disabled={(() => {
                                                                    const allMonths = getAllEventMonths();
                                                                    const currentIndex = allMonths.findIndex(m =>
                                                                        m.getFullYear() === currentMonth.getFullYear() &&
                                                                        m.getMonth() === currentMonth.getMonth()
                                                                    );
                                                                    return currentIndex <= 0;
                                                                })()}
                                                            >
                                                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                                                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </button>
                                                            <div className="text-sm font-semibold">
                                                                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const allMonths = getAllEventMonths();
                                                                    const currentIndex = allMonths.findIndex(m =>
                                                                        m.getFullYear() === currentMonth.getFullYear() &&
                                                                        m.getMonth() === currentMonth.getMonth()
                                                                    );
                                                                    if (currentIndex < allMonths.length - 1) {
                                                                        setCurrentMonth(allMonths[currentIndex + 1]);
                                                                    }
                                                                }}
                                                                className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                                                disabled={(() => {
                                                                    const allMonths = getAllEventMonths();
                                                                    const currentIndex = allMonths.findIndex(m =>
                                                                        m.getFullYear() === currentMonth.getFullYear() &&
                                                                        m.getMonth() === currentMonth.getMonth()
                                                                    );
                                                                    return currentIndex >= allMonths.length - 1;
                                                                })()}
                                                            >
                                                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                                                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        <div className="grid grid-cols-7 gap-1">
                                                            {/* Calendar headers */}
                                                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                                                <div key={day} className="text-xs text-gray-500 text-center py-1">
                                                                    {day}
                                                                </div>
                                                            ))}

                                                            {/* Calendar days */}
                                                            {(() => {
                                                                const allDays: Date[] = [];
                                                                const allStartDates: Date[] = [];
                                                                event?.data?.times?.forEach((range: any) => {
                                                                    const start = new Date(range.startDate);
                                                                    allStartDates.push(start);
                                                                    for (let d = new Date(start); d <= new Date(range.endDate); d.setDate(d.getDate() + 1)) {
                                                                        allDays.push(new Date(d));
                                                                    }
                                                                });

                                                                const earliestStart = new Date(Math.min(...allStartDates.map(d => d.getTime())));
                                                                const now = new Date();
                                                                const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
                                                                const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
                                                                const startPadding = firstDay.getDay();
                                                                const daysInMonth = lastDay.getDate();

                                                                const days = [];

                                                                // Add padding for first week
                                                                for (let i = 0; i < startPadding; i++) {
                                                                    days.push(<div key={`empty-${i}`} className="h-8"></div>);
                                                                }

                                                                // Add days of the month
                                                                for (let day = 1; day <= daysInMonth; day++) {
                                                                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                                                    const isInEventDays = allDays.some(d => d.toISOString() === date.toISOString());
                                                                    const isBeforeEventStart = date < earliestStart;
                                                                    const isPassed = isInEventDays && date < now;
                                                                    const isSelected = selectedTime === date.toISOString();
                                                                    const isAvailable = isInEventDays && !isBeforeEventStart && date >= now;

                                                                    let className = 'h-8 w-8 flex items-center justify-center text-sm rounded';
                                                                    let onClick: (() => void) | undefined = undefined;
                                                                    let onMouseDown: ((e: React.MouseEvent) => void) | undefined = undefined;

                                                                    if (isSelected) {
                                                                        className += ' bg-primary50 text-white cursor-pointer';
                                                                    } else if (isPassed) {
                                                                        className += ' bg-red-100 text-red-600 border border-red-200 cursor-not-allowed';
                                                                        onMouseDown = (e) => e.stopPropagation();
                                                                    } else if (isBeforeEventStart) {
                                                                        className += ' bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed';
                                                                        onMouseDown = (e) => e.stopPropagation();
                                                                    } else if (isAvailable) {
                                                                        className += ' bg-primary99 text-primary40 hover:bg-primary60 hover:text-white cursor-pointer';
                                                                        onClick = () => {
                                                                            setSelectedTime(date.toISOString());
                                                                            setShowAllTimes(false);
                                                                        };
                                                                        onMouseDown = (e) => e.stopPropagation();
                                                                    } else {
                                                                        className += ' text-gray-300 cursor-not-allowed';
                                                                        onMouseDown = (e) => e.stopPropagation();
                                                                    }

                                                                    days.push(
                                                                        <div
                                                                            key={day}
                                                                            className={className}
                                                                            onClick={onClick}
                                                                            onMouseDown={onMouseDown}
                                                                        >
                                                                            {day}
                                                                        </div>
                                                                    );
                                                                }

                                                                return days;
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <div className="px-4 py-2 text-xs text-gray-600">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                                                            <span>Passed dates</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
                                                            <span>Before event start</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-3 h-3 bg-primary99 rounded"></div>
                                                            <span>Available dates</span>
                                                        </div>
                                                    </div>
                                                    <button className="mt-4 px-4 py-2 rounded border border-gray-200 text-xs font-medium bg-white text-gray-700" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowAllTimes(false);
                                                    }}>Close</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        {/* --- Tickets Section --- */}
                        <div id="tickets" className="mb-6 mt-12 scroll-mt-24">
                            <div className="font-bold text-xl text-grey40 mb-2">Tickets</div>
                            {validationErrors.tickets && (
                                <div className="text-red-500 text-sm mb-2 flex items-center gap-2">
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor" />
                                    </svg>
                                    Please select at least one ticket
                                </div>
                            )}
                            {isEventPassed && (
                                <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
                                    <div className="text-red-800 font-semibold mb-1">Event Has Ended</div>
                                    <div className="text-red-600 text-sm">This event has already passed. Tickets are no longer available for purchase.</div>
                                </div>
                            )}
                            <div className="flex flex-col gap-4">
                                {!event?.data?.venueId && tickets?.map((ticket: any, i: number) => {
                                    const ticketId = ticket._id || i;
                                    const count = ticketCounts[ticketId] ?? 0;
                                    const soldOut = ticket.remained === 0;

                                    // Check ticket sale dates
                                    const now = new Date();
                                    const saleStartDate = ticket.saleStart ? new Date(ticket.saleStart) : null;
                                    const saleEndDate = ticket.saleEnd ? new Date(ticket.saleEnd) : null;

                                    const isSaleNotStarted = saleStartDate && now < saleStartDate;
                                    const isSaleEnded = saleEndDate && now > saleEndDate;
                                    const isTicketInactive = ticket.status === false;

                                    const isDisabled = isEventPassed || soldOut || isSaleNotStarted || isSaleEnded || isTicketInactive;
                                    return (
                                        <div key={i} className={`relative flex flex-col md:flex-row w-full sm:w-2/3 justify-between bg-white rounded ${validationErrors.tickets ? 'border-red-300 border-2 bg-red-50' : 'border-gray-100'} shadow-eventopia px-6 py-4 gap-3 ${isDisabled ? 'opacity-60' : ''} `}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-bold text-[15px] text-grey20">{ticket.name}</span>
                                                            {/* Ticket status badge */}
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${ticket.status
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                {ticket.status ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        {/* Remaining tickets count - only show if half of tickets are sold */}
                                                        {/* {typeof ticket.remained === 'number' && ticket.remained > 0 && !isEventPassed && ticket.status && (() => {
                                                            const totalQuantity = ticket.quantity || 0;
                                                            const soldTickets = totalQuantity - ticket.remained;
                                                            const halfSold = soldTickets >= (totalQuantity / 2);
                                                            return halfSold;
                                                        })() && (
                                                                <span className="text-xs bg-gray-100 text-gray-500 rounded px-2 py-0.5">{ticket.remained} left</span>
                                                            )} */}
                                                    </div>
                                                    {!isDisabled ? (
                                                        <div className="flex items-center gap-2">
                                                            <button className="w-8 h-8 rounded flex items-center justify-center text-lg font-bold text-grey80 bg-primary40 dark:bg-nightAccent dark:text-[#080808]" onClick={() => {
                                                                const newCount = Math.max(0, (ticketCounts[ticketId] ?? 0) - 1);
                                                                setTicketCounts(prevCounts => ({ ...prevCounts, [ticketId]: newCount }));
                                                                // Clear tickets validation error when user removes a ticket and there are still tickets selected
                                                                if (validationErrors.tickets && newCount > 0) {
                                                                    setValidationErrors(prev => ({ ...prev, tickets: false }));
                                                                }
                                                            }} disabled={count <= 0}><Minus color={'#fff'} /></button>
                                                            <span className="font-bold text-sm w-6 text-center text-grey20">{count}</span>
                                                            <button data-testid="add-ticket-button" className="w-8 h-8 rounded flex items-center justify-center text-lg font-bold text-grey80 bg-primary40 dark:bg-nightAccent dark:text-[#080808]"
                                                                onClick={() => {
                                                                    // Optimistically update UI
                                                                    setTicketCounts(counts => ({ ...counts, [ticketId]: Math.min(ticket.remained, (counts[ticketId] ?? 0) + 1) }));
                                                                    // Only call API if this ticket is already in the cart
                                                                    /*  const isInCart = cart && cart.some((item: any) => item.ticketId === ticket._id && item.eventId === event?.data?._id);
                                                                     if (isInCart) {
                                                                         addToCartMutation.mutate({
                                                                             eventId: event?.data?._id,
                                                                             items: [{ ticketId: ticket._id, quantity: 1 }],
                                                                         });
                                                                     } */
                                                                    // Clear tickets validation error when user adds a ticket
                                                                    if (validationErrors.tickets) {
                                                                        setValidationErrors(prev => ({ ...prev, tickets: false }));
                                                                    }
                                                                }}
                                                                disabled={count >= ticket.remained}>
                                                                <Plus color={'#fff'} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-base text-[#CB0000] font-bold mt-1">
                                                            {isEventPassed ? 'Event Ended' :
                                                                soldOut ? 'Sold Out' :
                                                                    isSaleNotStarted ? 'Sale Not Started' :
                                                                        isSaleEnded ? 'Sale Ended' :
                                                                            isTicketInactive ? 'Ticket Inactive' : 'Unavailable'}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="font-bold text-base text-primary50">{cur}{ticket.price}</span>
                                                <div className="text-xs text-grey30 mb-1">{ticket.description}</div>

                                                {/* Ticket sale date information */}
                                                {(ticket.saleStart || ticket.saleEnd) && (
                                                    <div className="text-xs text-grey40 mt-1">
                                                        {ticket.saleStart && (
                                                            <div>Sale starts: {new Date(ticket.saleStart).toLocaleDateString()} {new Date(ticket.saleStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                        )}
                                                        {ticket.saleEnd && (
                                                            <div>Sale ends: {new Date(ticket.saleEnd).toLocaleDateString()} {new Date(ticket.saleEnd).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                        )}
                                                    </div>
                                                )}



                                            </div>
                                            {count > 0 && (
                                                <div className="absolute bottom-4 right-6">
                                                    <span className="font-bold text-lg text-primary50">
                                                        Total: {cur}{(count * ticket.price).toFixed(2)}
                                                    </span>
                                                </div>
                                            )}

                                        </div>
                                    );
                                })}
                            </div>

                            <div>
                                {/* Seat Map Selection */}
                                {event?.data?.venueId && (
                                    <div className="mt-3">


                                        <div className="mt-3">
                                            <SeatMapSelector
                                                eventId={event?.data?._id || ''}
                                                venueId={event?.data?.venueId || ''}
                                                onSeatSelect={handleSeatSelect}
                                                onSeatDeselect={handleSeatDeselect}
                                                selectedSeats={selectedSeats}
                                                seatMapType="client"
                                                tickets={tickets || []}
                                                currency={event?.data?.currency}
                                                onSeatAssignmentsLoaded={handleSeatAssignmentsLoaded}
                                            />

                                            {/* Selected Seats Display */}
                                            {selectedSeats && selectedSeats.length > 0 && (
                                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                                                    <div className="text-sm font-medium text-blue-800 mb-2">Selected Seats:</div>
                                                    <div className="space-y-2">
                                                        {selectedSeats.map((seatUuid, index) => {
                                                            // Find the ticket information for this seat
                                                            const seatAssignment = seatAssignments?.find(assignment => assignment.seatUuid === seatUuid);
                                                            const ticketInfo = seatAssignment ? tickets?.find((ticket: any) => ticket._id === seatAssignment.ticketId) : null;

                                                            // Extract seat details from venue data
                                                            let seatDetails = {
                                                                zone: 'Unknown Zone',
                                                                row: 'Unknown Row',
                                                                seat: 'Unknown Seat',
                                                                zoneCode: ''
                                                            };

                                                            if (venueResponse?.data?.seatMap && !venueLoading) {
                                                                venueResponse.data.seatMap.zones?.forEach((zoneData: any) => {
                                                                    zoneData.rows?.forEach((rowData: any) => {
                                                                        rowData.seats?.forEach((seatData: any) => {
                                                                            const seatUuidFromMap = seatData.uuid || seatData.seat_guid || `${zoneData.name}-${rowData.row_number}-${seatData.seat_number}`;
                                                                            if (seatUuidFromMap === seatUuid) {
                                                                                seatDetails = {
                                                                                    zone: zoneData.name,
                                                                                    row: rowData.row_number,
                                                                                    seat: seatData.seat_number,
                                                                                    zoneCode: zoneData.code || ''
                                                                                };
                                                                            }
                                                                        });
                                                                    });
                                                                });
                                                            }

                                                            return (
                                                                <div key={seatUuid} className="bg-white p-3 rounded border border-blue-200">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-1">
                                                                            <div className="text-sm font-medium text-gray-800 mb-1">
                                                                                {seatDetails.zone} {seatDetails.zoneCode && `(${seatDetails.zoneCode})`} - Row {seatDetails.row}, Seat {seatDetails.seat}
                                                                            </div>
                                                                            {ticketInfo && (
                                                                                <div className="text-xs text-gray-600 mt-1">
                                                                                    <div className="font-medium">{ticketInfo.name}</div>
                                                                                    <div className="text-green-600 font-semibold">{cur}{ticketInfo.price}</div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleSeatDeselect(seatUuid)}
                                                                            className="text-red-500 hover:text-red-700 text-xs ml-2"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                )}
                            </div>

                        </div>
                        <div id="location" className="mb-6 mt-12 scroll-mt-24">
                            <div className="font-bold text-lg sm:text-xl text-grey40 mb-2">Location</div>
                            <div className="text-sm text-grey20 font-bold mb-1 flex"><Location />{event?.data?.venue}</div>
                            <div className="text-[15px] text-grey30 mb-1">
                                {event?.data?.showOnlyCity === 1
                                    ? event?.data?.address?.city || event?.data?.address?.fullAddress
                                    : event?.data?.address?.fullAddress
                                }
                            </div>
                            {event?.data?.lat && event?.data?.lng ? (
                                <EventMap
                                    lat={event.data.lat}
                                    lng={event.data.lng}
                                    title={event.data.title}
                                    address={event.data.address}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                        {event?.data?.howToGetThere &&
                            <div id="how-to-get-there" className="mb-6 scroll-mt-24">
                                <div className="font-semibold text-lg sm:text-xl text-grey60 mb-2">How to get there & Parking</div>
                                <div className="text-[15px] text-grey30">{event?.data?.howToGetThere}</div>
                            </div>
                        }
                        {event?.data?.refundPolicy &&
                            <div id="refund-policy" className="mb-6 scroll-mt-24">
                                <div className="font-bold text-lg sm:text-xl text-grey40 mb-2">Refund policy</div>
                                <div className="text-[15px] text-grey30">{event?.data?.refundPolicy}</div>
                            </div>
                        }
                        {event?.data?.faqs && event?.data?.faqs.length > 0 && (
                            <div id="faq" className="mb-6 scroll-mt-24">
                                <div className="font-bold text-lg sm:text-xl text-grey40 mb-4">Frequently Asked Questions</div>
                                <div className="space-y-2">
                                    {event.data.faqs.map((faq: any, index: number) => (
                                        <div key={index} className="border border-gray-200 rounded bg-white shadow-sm">
                                            <button
                                                onClick={() => toggleFaq(index)}
                                                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors duration-200"
                                            >
                                                <div className="font-semibold text-grey40 text-[15px] pr-4">
                                                    {faq.question}
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <svg
                                                        className={`w-5 h-5 text-grey40 transition-transform duration-200 ${expandedFaqs[index] ? 'rotate-180' : ''
                                                            }`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </div>
                                            </button>
                                            {expandedFaqs[index] && (
                                                <div className="px-4 pb-4">
                                                    <div className="text-grey30 text-[15px] leading-relaxed border-t border-gray-100 pt-3">
                                                        {faq.answer}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div id="about" className="mb-6 scroll-mt-24">
                            <div className="font-bold text-lg sm:text-xl text-grey40 mb-2">About</div>

                            <div className="font-vazirmatn text-[15px] text-grey30 whitespace-pre-line">{event?.data?.description}</div>

                        </div>
                        {/* --- Comments Section --- */}
                        {event?.data?.commentsDisabled !== 1 && (
                            <div className="mb-6">
                                <div className="font-bold text-lg sm:text-xl text-grey40 mb-2">
                                    Comments {comments.length > 0 && `(${comments.length})`}
                                </div>

                                {/* Comment input - only for logged in users */}
                                {isAuthenticated ? (
                                    <div className="mb-4">
                                        <Input
                                            placeholder="Add a comment..."
 classNames={inputClassNames}
                                            value={commentInput}
                                            onChange={e => setCommentInput(e.target.value)}
                                            disabled={postComment.isPending}
                                            endContent={
                                                <button
                                                    className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                    onClick={() => commentInput && postComment.mutate(commentInput)}
                                                    disabled={!commentInput || postComment.isPending}
                                                >
                                                    {postComment.isPending ? (
                                                        <Loading
                                                            width={20}
                                                            height={20}
                                                            color='#009CA6'
                                                        />
                                                    ) : (
                                                        commentInput && <Send
                                                            width={20}
                                                            height={20}
                                                            color={commentInput && !postComment.isPending ? '#009CA6' : "#9CA3AF"}
                                                        />
                                                    )}
                                                </button>
                                            }
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded p-4 mb-4 text-center">
                                        <p className="text-gray-600 text-sm mb-2">Please log in to post a comment</p>
                                        <Link to="/login" className="text-primary50 font-semibold text-sm hover:underline">
                                            Login to comment
                                        </Link>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    {comments.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            <p className="text-sm">No comments yet. Be the first to comment!</p>
                                        </div>
                                    ) : (
                                        comments.map((c: any, i: number) => (
                                            <div key={c.id || c._id || i} className="bg-white rounded p-4  border-gray-100 shadow-sm">
                                                <div className="flex items-start gap-3 mb-1">
                                                    <Avatar
                                                        src={c.userInfo?.profileImage}
                                                        name={c.userInfo?.firstName}
                                                        seed={c.userInfo?.userId || c.userInfo?._id || c.userInfo?.id || c.userInfo?.email}
                                                        size={40}
                                                        className="border"
                                                    />

                                                    <div className="flex flex-col">
                                                        <div className="font-bold text-[15px] text-grey20">{c.userInfo?.firstName || 'Anonymous'}</div>
                                                        <div className="text-xs text-grey50 font-semibold">{moment(c.createdAt).fromNow()}</div>
                                                    </div>
                                                </div>
                                                <div className="text-grey30 text-[15px] mb-2">{c.body}</div>
                                                {isAuthenticated ? (
                                                    <button
                                                        className="text-sm text-primary40 font-bold mb-2"
                                                        onClick={() => setReplyInputs((r) => ({ ...r, [c.id || c._id]: r[c.id || c._id] ? '' : 'show' }))}
                                                    >Reply</button>
                                                ) : (
                                                    <Link to="/login" className="text-xs text-primary50 font-semibold mb-2 hover:underline">
                                                        Login to reply
                                                    </Link>
                                                )}
                                                {/* Reply input */}
                                                {replyInputs[c.id || c._id] && (
                                                    <div className="ml-8 mb-4">
                                                        <Input
                                                            placeholder="Write a reply..."
 classNames={inputClassNames}
                                                            value={replyInputs[c.id || c._id] === 'show' ? '' : (replyInputs[c.id || c._id] || '')}
                                                            onChange={e => setReplyInputs((r) => ({ ...r, [c.id || c._id]: e.target.value }))}
                                                            disabled={postReply.isPending}
                                                            endContent={
                                                                <button
                                                                    className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                                    onClick={() => {
                                                                        const replyText = replyInputs[c.id || c._id];
                                                                        if (replyText && replyText !== 'show') {
                                                                            postReply.mutate({ parentCommentId: c.id || c._id, text: replyText });
                                                                        }
                                                                    }}
                                                                    disabled={!replyInputs[c.id || c._id] || replyInputs[c.id || c._id] === 'show' || postReply.isPending}
                                                                >
                                                                    {postReply.isPending ? (
                                                                        <Loading
                                                                            width={20}
                                                                            height={20}
                                                                            color="#0EA5E9"
                                                                        />
                                                                    ) : (
                                                                        <Send
                                                                            width={20}
                                                                            height={20}
                                                                            color={replyInputs[c.id || c._id] && replyInputs[c.id || c._id] !== 'show' && !postReply.isPending ? "#0EA5E9" : "#9CA3AF"}
                                                                        />
                                                                    )}
                                                                </button>
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {/* Replies */}
                                                {c.replies?.length > 0 && (
                                                    <div className="ml-8 mt-2 space-y-2">
                                                        {c.replies.map((r: any, j: number) => (
                                                            <div key={r.id || r._id || j} className="bg-white rounded p-3 border-gray-100">
                                                                <div className="flex items-start gap-2 mb-1">
                                                                    <Avatar
                                                                        src={r.userInfo?.profileImage}
                                                                        name={r.userInfo?.firstName}
                                                                        seed={r.userInfo?.userId || r.userInfo?._id || r.userInfo?.id || r.userInfo?.email}
                                                                        size={40}
                                                                        className="border"
                                                                    />
                                                                    <div className="flex flex-col">
                                                                        <div className="font-bold text-[15px] text-grey20">{r.userInfo?.firstName || 'Anonymous'}</div>
                                                                        <div className="text-xs text-grey50 font-semibold">{moment(r.createdAt).fromNow()}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="text-grey30 text-[15px] mb-2">{r.body}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Right: Sidebar */}
                    <div className="w-full md:w-80 flex flex-col gap-6 sticky top-[8px] self-start">
                        <div className="hidden md:block bg-white rounded shadow-eventopia p-6 gap-4">
                            <div className="flex items-start justify-start flex-col gap-2">
                                <span className="font-bold text-base text-primary20">From {cur}{event?.data?.price}</span>
                                {isEventPassed ? (
                                    <div className="bg-gray-100 text-gray-500 w-full px-6 py-2 rounded font-semibold text-center">
                                        Event Has Ended
                                    </div>
                                ) : isSelectedTimePassed ? (
                                    <div className="bg-gray-100 text-gray-500 w-full px-6 py-2 rounded font-semibold text-center">
                                        Selected Date Has Passed
                                    </div>
                                ) : (
                                    <Button
                                        className="bg-primary50 text-white w-full px-6 py-3 h-[48px] text-base rounded font-bold disabled:opacity-60 dark:bg-nightAccent dark:text-[#080808]"
                                        onClick={() => {
                                            // Reset validation errors
                                            setValidationErrors({});

                                            // Check for validation errors
                                            const errors: { date?: boolean; tickets?: boolean } = {};
                                            if (!selectedTime) {
                                                errors.date = true;
                                            }
                                            if (!hasAnySelection) {
                                                errors.tickets = true;
                                            }

                                            if (Object.keys(errors).length > 0) {
                                                setValidationErrors(errors);
                                                setShowCheckoutWarning(true);

                                                // Scroll to first error
                                                if (errors.date) {
                                                    document.getElementById('date-time')?.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'center'
                                                    });
                                                } else if (errors.tickets) {
                                                    document.getElementById('tickets')?.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'center'
                                                    });
                                                }
                                                return;
                                            }

                                            setShowCheckoutWarning(false);
                                            checkoutMutation.mutate();
                                        }}
                                        disabled={checkoutMutation.isPending}
                                    >
                                        {checkoutMutation.isPending ? 'Adding to Cart...' :
                                            `Checkout $${(totalPrice || 0).toFixed(2)}${event?.data?.venueId && selectedSeats.length > 0 ? ` (${selectedSeats.length} seat${selectedSeats.length > 1 ? 's' : ''})` : ''}`}
                                    </Button>
                                )}
                            </div>
                            {showCheckoutWarning && !isEventPassed && !isSelectedTimePassed && (
                                <div className="text-red-500 text-xs mt-4">
                                    {!selectedTime && !hasAnySelection && "Please select a date/time and at least one ticket or seat before checking out."}
                                    {!selectedTime && hasAnySelection && "Please select a date/time before checking out."}
                                    {selectedTime && !hasAnySelection && "Please select at least one ticket or seat before checking out."}
                                </div>
                            )}
                            {isEventPassed && (
                                <div className="text-gray-500 text-xs mt-4">This event has already ended. Tickets are no longer available for purchase.</div>
                            )}
                            {isSelectedTimePassed && !isEventPassed && (
                                <div className="text-red-500 text-xs mt-4">The selected date has already passed. Please choose a future date.</div>
                            )}
                        </div>
                        <div className="bg-white rounded flex flex-col">
                            <div className="font-semibold text-[17px] text-grey60 mb-2">Organized by</div>
                            <div className="flex items-center gap-3 bg-[#F8F8F8] p-4 rounded">
                                {!event?.data?.info?.orgName ? (
                                    <Link
                                        to={`/user/${event?.data?.info.id}`}
                                        className="flex items-center gap-[6px] hover:underline"
                                    >
                                        <Avatar
                                            src={event?.data?.info.profileImage}
                                            name={event?.data?.info.firstName}
                                            seed={event?.data?.info.id}
                                            size={48}
                                            className="border border-gray-200"
                                        />
                                        <span className="text-xs text-grey20 font-normal">{event?.data?.info.firstName}</span>
                                    </Link>
                                ) : null}
                                {event?.data?.info?.orgName ? (
                                    <Link
                                        to={`/organization/${event?.data?.info._id}`}
                                        className="flex items-center gap-[6px] hover:underline"
                                    >
                                        <img
                                            src={event?.data?.info?.profileImage || event?.data?.info?.attachments?.avatar?.link || '/host-avatar.png'}
                                            alt={event?.data?.info?.orgName}
                                            className="w-12 h-12 rounded-full object-cover border border-gray-200"
                                        />
                                        <span className="text-xs text-grey20 font-normal">{event?.data?.info?.orgName}</span>
                                    </Link>
                                ) : null}
                                {/*  <Link href={`/host/${event?.data?.info?.id?.replace(/\W+/g, '-').toLowerCase()}`}
                                    className="flex items-center gap-3 hover:underline">
                                    <img src={event?.data?.info?.profileImage || event?.data?.info?.profileImage || '/host-avatar.png'} alt={event?.data?.info?.firstName || event?.data?.info?.orgName} className="w-12 h-12 rounded-full object-cover border" />
                                    <div>
                                        <div className="font-semibold text-[15px] text-grey20">{event?.data?.info?.orgName || event?.data?.info?.firstName}</div>
                                        
                            </div>
                        </Link> */}
                                {/* <div className="text-xs text-gray-500">{event?.data?.userInfo?.followers}k followers</div> */}
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                                {/* <button
                                    onClick={() => setShowShareModal(true)}
                                    className="text-xs text-grey40 underline font-semibold flex gap-2"
                                >
                                    <Share color={'#687879'} />
                                    Share Event
                                </button> */}
                                {isAuthenticated && (
                                    <button
                                        onClick={() => setShowReportModal(true)}
                                        className="text-xs text-grey40 underline font-semibold flex gap-2 hover:text-red-600 transition-colors"
                                    >
                                        <Report color={'#687879'} />
                                        Report This Event
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* More Events Like This */}
                {
                    relatedEvents.length > 0 && (
                        <div className="mt-12">
                            <div className="font-semibold text-lg mb-4">More events Like this</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                                {relatedEvents.map((rel: any, i: number) => {
                                    const eventUrl = rel.handle ? `/e/${rel.handle}` : `/event/${rel._id || rel.id}`;
                                    return (
                                        <Link key={i} to={eventUrl} className="block group">
                                            <div className="bg-white rounded p-0 flex flex-col relative overflow-hidden group-hover:shadow-lg transition-shadow">
                                                <div className="relative">
                                                    <img src={rel.image} alt={rel.title} className="w-full h-36 object-cover" />
                                                    {rel.isNew && (
                                                        <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">New</span>
                                                    )}
                                                    <LikeButton
                                                        eventId={rel._id || rel.id || ''}
                                                        initialLikes={rel.likes || 0}
                                                        likeStats={rel.likeStats}
                                                        className="absolute bottom-3 right-3 bg-white/90 text-gray-700 text-xs px-2 py-1 rounded shadow"
                                                        refetchQueries={['event', 'relatedEvents']}
                                                        onLikeChange={(likes, isLiked) => {
                                                            console.log(`Related event ${rel._id || rel.id} like status changed: ${likes} likes, isLiked: ${isLiked}`);
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-2 px-4 pt-3 pb-4 flex-1">
                                                    <div className="font-semibold text-base mb-1 line-clamp-2 group-hover:text-primary50">{rel.title}</div>
                                                    <div className="text-xs text-gray-500 mb-1">{rel.date}</div>
                                                    <div className="text-xs text-gray-500 mb-1">{rel.location}</div>
                                                    <div className="flex items-center justify-between mt-auto pt-2">
                                                        <span className="font-bold text-primary50 text-base">{rel.price}</span>
                                                        <div className="flex gap-1">
                                                            <button
                                                                className="bg-gray-100 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-200 transition-colors"
                                                                tabIndex={-1}
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    const shareUrl = rel.handle ? `${getBaseUrl()}/e/${rel.handle}` : `${getBaseUrl()}/event/${rel._id || rel.id}`;
                                                                    navigator.clipboard.writeText(shareUrl);
                                                                }}
                                                                title="Share event"
                                                            >
                                                                <Share width={14} height={14} />
                                                            </button>
                                                            <button className="bg-primary50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold dark:bg-nightAccent dark:text-[#080808]" tabIndex={-1} onClick={e => e.preventDefault()}>+</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )
                }
            </div >

            {/* Sticky Checkout Section - Mobile Only */}
            < div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg" >
                <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg text-primary20">From {cur}{event?.data?.price}</span>
                        <span className="text-sm text-gray-500">Total: {cur}{totalPrice.toFixed(2)}</span>
                    </div>
                    {isEventPassed ? (
                        <div className="bg-gray-100 text-gray-500 w-full px-6 py-3 rounded font-semibold text-center">
                            Event Has Ended
                        </div>
                    ) : isSelectedTimePassed ? (
                        <div className="bg-gray-100 text-gray-500 w-full px-6 py-3 rounded font-semibold text-center">
                            Selected Date Has Passed
                        </div>
                    ) : (
                        <Button
                            className="bg-primary50 text-white w-full px-6 py-3 h-[48px] text-base rounded font-bold disabled:opacity-60 dark:bg-nightAccent dark:text-[#080808]"
                            onClick={() => {
                                // Reset validation errors
                                setValidationErrors({});

                                // Check for validation errors
                                const errors: { date?: boolean; tickets?: boolean } = {};
                                if (!selectedTime) {
                                    errors.date = true;
                                }
                                if (!hasAnySelection) {
                                    errors.tickets = true;
                                }

                                if (Object.keys(errors).length > 0) {
                                    setValidationErrors(errors);
                                    setShowCheckoutWarning(true);

                                    // Scroll to first error
                                    if (errors.date) {
                                        document.getElementById('date-time')?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'center'
                                        });
                                    } else if (errors.tickets) {
                                        document.getElementById('tickets')?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'center'
                                        });
                                    }
                                    return;
                                }

                                setShowCheckoutWarning(false);
                                checkoutMutation.mutate();
                            }}
                            disabled={checkoutMutation.isPending}
                        >
                            {checkoutMutation.isPending ? 'Adding to Cart...' :
                                `Checkout $${(totalPrice || 0).toFixed(2)}${event?.data?.venueId && selectedSeats.length > 0 ? ` (${selectedSeats.length} seat${selectedSeats.length > 1 ? 's' : ''})` : ''}`}
                        </Button>
                    )}
                    {showCheckoutWarning && !isEventPassed && !isSelectedTimePassed && (
                        <div className="text-red-500 text-xs mt-2 text-center">
                            {!selectedTime && !hasAnySelection && "Please select a date/time and at least one ticket or seat before checking out."}
                            {!selectedTime && hasAnySelection && "Please select a date/time before checking out."}
                            {selectedTime && !hasAnySelection && "Please select at least one ticket or seat before checking out."}
                        </div>
                    )}
                    {isEventPassed && (
                        <div className="text-gray-500 text-xs mt-2 text-center">This event has already ended. Tickets are no longer available for purchase.</div>
                    )}
                    {isSelectedTimePassed && !isEventPassed && (
                        <div className="text-red-500 text-xs mt-2 text-center">The selected date has already passed. Please choose a future date.</div>
                    )}
                </div>
            </div>



            {/* Floating Action Buttons */}
            <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
                {/* Share Button */}
                {/* <button
                    onClick={() => setShowShareModal(true)}
                    className="bg-primary50 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-primary60 transition-all duration-300 hover:scale-110"
                    aria-label="Share event"
                >
                    <Share width={20} height={20} />
                </button> */}

                {/* Back to Top Button */}
                {showBackToTop && (
                    <button
                        onClick={() => {
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                        }}
                        className="bg-gray-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110"
                        aria-label="Back to top"
                    >
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
                        </svg>
                    </button>
                )}
            </div>



            {/* Share Modal */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                eventTitle={event?.data?.title || 'Event'}
                eventUrl={event?.data?.handle ? getBaseUrl() + `/e/${event?.data?.handle}` : getBaseUrl() + location.pathname}
                eventImage={event?.data?.attachments?.banner?.link}
                eventDescription={event?.data?.description}
            />

            {/* Report Event Modal */}
            <ReportEventModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                onSubmit={reportEventMutation.mutate}
                isSubmitting={reportEventMutation.isPending}
            />
        </div >
    );
} 